<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Title</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.titleContent" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Description</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.descContent" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Preview Image</label>
          <div class="group-item">
            <UploadImage
              :image="{ imageSrc: fields.image }"
              :height="`200`"
              :typeUpload="'image'"
              @callback="obj => (fields.image = obj.imageSrc)"
              @callbackDelete="fields.image = ''"
            />
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Features (max {{ MAX_FEATURE_ITEMS }})</label>
            <v-btn small color="primary" :disabled="fields.items.length >= MAX_FEATURE_ITEMS" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Feature</span>
            </v-btn>
          </div>
          <div class="group-item mb-3">
            <v-row>
              <v-col cols="12" md="6">
                <ColorPicker
                  label="Border Color"
                  :value="fields.colorBorderItem"
                  name="cefloreco-product-world-color-border"
                  default-color="#bebebe"
                  @input="value => (fields.colorBorderItem = value)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <ColorPicker
                  label="Highlight Color"
                  :value="fields.colorHighlightItem"
                  name="cefloreco-product-world-color-highlight"
                  default-color="#ff7657"
                  @input="value => (fields.colorHighlightItem = value)"
                />
              </v-col>
            </v-row>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.items" :key="`feature-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>Feature {{ index + 1 }}</span>
                    <v-btn
                      icon
                      :disabled="fields.items.length <= MIN_FEATURE_ITEMS"
                      @click.stop="handleRemoveItem(index)"
                    >
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <label>Number</label>
                  <v-text-field v-model="item.number" hide-details class="mt-0 mb-3" />
                  <label>Content</label>
                  <ckeditor :editor="editor" v-model="item.content" :config="editorConfig" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';
import ColorPicker from '../common/ui-modules/inputs/color-picker.vue';

const MAX_FEATURE_ITEMS = 6;
const MIN_FEATURE_ITEMS = 1;

const buildItemDefault = () => ({
  number: '01',
  content:
    '<h4>Product Strategy</h4><p>Turning ideas and user insights into products built for real market opportunities.</p>',
});

const getDefaultFields = () => ({
  title: 'Cefloreco Product World Section',
  settings: {
    paddingTop: false,
    paddingBottom: false,
    backgroundColor: '#000000',
    textColor: '#FFFFFF',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: true,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  titleContent: '<p>Our product universe</p><h2>One studio.<br>Many everyday<br>experiences.</h2>',
  descContent: '<p>From entertainment and creativity to learning and utility, we build digital products.</p>',
  image: '',
  colorBorderItem: '#bebebe',
  colorHighlightItem: '#ff7657',
  items: [buildItemDefault()],
});

export default {
  name: 'CeflorecoProductWorldSectionEditor',
  components: { UploadImage, SectionLayout, ColorPicker },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
      MAX_FEATURE_ITEMS,
      MIN_FEATURE_ITEMS,
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      items: extract.items && extract.items.length ? extract.items.slice(0, MAX_FEATURE_ITEMS) : def.items,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCeflorecoProductWorld(this.sectionEditor.section.html, {
        ...this.fields,
        items: (this.fields.items || []).slice(0, MAX_FEATURE_ITEMS),
      });
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddItem() {
      if (this.fields.items.length >= MAX_FEATURE_ITEMS) return;
      const next = buildItemDefault();
      next.number = String(this.fields.items.length + 1).padStart(2, '0');
      this.fields.items.push(next);
    },
    handleRemoveItem(index) {
      if (this.fields.items.length <= MIN_FEATURE_ITEMS) return;
      this.fields.items.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.ck-editor__editable_inline,
.ck-editor__editable {
  min-height: 140px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
